import lodashFind from "lodash/find";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashUniqBy from "lodash/uniqBy";

import { ENDPOINT_CONFIG } from "@/common/constants/api";
import { SVG_ICONS_TOPICS } from "@/common/constants/topic_icons_svg";
import { httpGet } from "@/services/api.service";
import { formatFeature } from "@/services/media/news.service";
import { isFeatureUnlocked } from "@/common/featureLock.js";
import { upperCaseFirstLetter } from "@/common/basic.utilities";

export { _addDisplayNameAliases as addDisplayNameAliases };
export { _convertTopicScoresFromFieldToApiName as convertTopicScoresFromFieldToApiName };
export { _determinePrimaryTopics as determinePrimaryTopics };
export { _findInfluence as findInfluence };
export { _findTopic as findTopic };
export { _findTopicAllFlatten as findTopicAllFlatten };
export { _formatCategoriesForV2 as formatCategoriesForV2 };
export { _formatFeatures as formatFeatures };
export { _getAllPrimaryTopics as getAllPrimaryTopics };
export { _getInfluencersArray as getInfluencersArray };
export { _getInfluencesArray as getInfluencesArray };
export { _getPrimaryTopics as getPrimaryTopics };
export { _getTopicIcon as getTopicIcon };
export { _getTopicImmediateChildren as getTopicImmediateChildren };
export { _getTopicKey as getTopicKey };
export { _getTopicType as getTopicType };
export { _getTopicsArray as getTopicsArray };
export { _getTopicsArraySpecial as getTopicsArraySpecial };
export { _getTopicsByCodes as getTopicsByCodes };
export { _getTopicsConfig as getTopicsConfig };
export { _getTopicsStringFromCategories as getTopicsStringFromCategories };
export { _getTopicsStringFromTopicsScores as getTopicsStringFromTopicsScores };
export { _isSubtopicSelected as isSubtopicSelected };
export { _isTopicSelected as isTopicSelected };
export { _modifyTopicString as modifyTopicString };
export { _removeMissingTopics as removeMissingTopics };
export { _unjoinTopicScores as unjoinTopicScores };

let topicsConfig = null;

const _formatFeatures = (features, params) => {
  if (features) {
    const LEN = features.length;
    for (let i = 0; i < LEN; i++) {
      let feature = features[i];
      formatFeature(feature, params);
    }
  }
};

const _getTopicsByCodes = (codes) => {
  let topics = [];
  if (codes && Array.isArray(codes)) {
    codes.forEach((code) => {
      const TOPIC = _findTopic(code, true, false);
      topics.push(TOPIC);
    });
  }
  return topics;
};

const _determinePrimaryTopics = (
  topicScores,
  isTwitter,
  acceptablePrimaryTopics
) => {
  if (topicScores) {
    const entries = Object.entries(topicScores);
    entries.sort(function (a, b) {
      return b[1] - a[1];
    });

    let topics = [];
    for (let i = 0; i < entries.length; i++) {
      const key = _getTopicKey(entries[i][0], false);
      if (
        _considerPrimaryTopic(
          key,
          isTwitter,
          entries[i][1],
          acceptablePrimaryTopics
        )
      ) {
        topics.push(key);
      }
    }
    if (topics.length) {
      return topics;
    }
  }
  return null;
};

const _considerPrimaryTopic = (
  topicName,
  isTwitter,
  score,
  acceptablePrimaryTopics
) => {
  let PRIMARY_TOPICS = _getAllPrimaryTopics();
  let tArray = lodashCloneDeep(PRIMARY_TOPICS);
  let isInfluence = false;
  if (isFeatureUnlocked("influences")) {
    const INFLUENCE = _findInfluence(topicName);
    if (INFLUENCE) {
      const INFLUENCES_ARRAY = _getInfluencesArray();
      tArray = lodashCloneDeep(INFLUENCES_ARRAY);
      isInfluence = true;
    }
  }
  for (let i = 0; i < tArray.length; i++) {
    if (tArray[i].type === topicName) {
      let threshold = tArray[i].defaultNewsMatchThreshold;
      if (isTwitter) {
        threshold = tArray[i].defaultTwitterMatchThreshold;
      }
      if (
        score >= threshold &&
        (acceptablePrimaryTopics.indexOf(tArray[i].type) > -1 ||
          acceptablePrimaryTopics === null)
      ) {
        return true;
      }
    }
  }
  return false;
};

// this function is used to handle the mismatch of topic keys since
// server accepts disaster while the front end expects disasters.
// the outgoing param means it might need to call the api
const _getTopicKey = (input, outgoing) => {
  let key = input ? input.toLowerCase() : null;
  if (
    input === "natural_disaster" ||
    input === "disaster" ||
    input === "disasters"
  ) {
    key = outgoing ? "disaster" : "disasters";
  }
  return key;
};

const _findTopic = (input, getKey, outgoing) => {
  let PRIMARY_TOPICS = _getAllPrimaryTopics();
  return _findTopicOrSubtopic(input, getKey, outgoing, PRIMARY_TOPICS);
};

const _findTopicAllFlatten = (input, getKey, outgoing) => {
  return _findTopicOrSubtopic(input, getKey, outgoing, _getTopicsArray());
};

const _findInfluence = (input) => {
  return _findTopicOrSubtopic(input, "apiName", null, _getInfluencesArray());
};

const _findInfluencer = (input) => {
  const INFLUENCERS = _getInfluencersArray();
  return lodashFind(INFLUENCERS, (influencer) => {
    if (input === influencer.type) {
      return influencer;
    }
  });
};

const _findTopicOrSubtopic = (input, getKey, outgoing, topics) => {
  return lodashFind(topics, (topic) => {
    let key = input;
    if (getKey) {
      key = _getTopicKey(input, outgoing);
    }
    if (topic.type) {
      return topic.type.toUpperCase() === key.toUpperCase();
    }
    return topic.apiName.toUpperCase() === key.toUpperCase();
  });
};

const _getTopicsStringFromTopicsScores = (
  topicScores,
  thresholdType,
  isInfluence
) => {
  let dataNeeded = _formatTopicScoresForV2(
    topicScores,
    thresholdType,
    isInfluence
  );
  let res = "";
  for (let i = 0; i < dataNeeded.length; i++) {
    if (i > 0) {
      res += " | ";
    }
    res += dataNeeded[i].primary;
    if (dataNeeded[i].subtopics) {
      res += " > " + dataNeeded[i].subtopics.join(", ");
    }
  }
  return res;
};

const _getThreshold = (topic, thresholdType, isInfluence) => {
  let thresholdField = "defaultNewsMatchThreshold";
  if (thresholdType === "twitter") {
    thresholdField = "defaultTwitterMatchThreshold";
  }
  if (isInfluence) {
    const INFLUENCE = _findInfluence(topic.apiName);
    if (INFLUENCE) {
      return topic[thresholdField];
    }
    return null;
  }
  if (topic[thresholdField] && topic[thresholdField] > 0) {
    return topic[thresholdField];
  }
  throw new Error("couldn't determine threshold for topic ", topic);
};

const _formatTopicScoresForV2 = (
  itemTopicScores,
  thresholdType,
  isInfluence
) => {
  let res = [];
  const PRIMARY_TOPICS = lodashCloneDeep(_getPrimaryTopics());
  const INFLUENCES_ARRAY = lodashCloneDeep(_getInfluencesArray());
  const topicsAndInfluences = PRIMARY_TOPICS.concat(INFLUENCES_ARRAY);
  for (let i = 0; i < topicsAndInfluences.length; i++) {
    let match = null;
    const currentTopic = topicsAndInfluences[i];
    const currentTopicNameFixed = _getTopicKey(currentTopic.apiName, true);

    let threshold = _getThreshold(currentTopic, thresholdType, isInfluence);
    const INFLUENCE = _findInfluence(currentTopic.apiName);

    if (itemTopicScores[currentTopicNameFixed] >= threshold) {
      let text;
      if (!isInfluence) {
        if (!INFLUENCE) {
          text = currentTopic.singular;
        }
      } else {
        if (INFLUENCE && INFLUENCE.influencerType) {
          const INFLUENCER = _findInfluencer(INFLUENCE.influencerType);
          if (INFLUENCER && INFLUENCER.name) {
            text = INFLUENCER.name;
          }
        }
      }
      if (text) {
        match = {
          primary: text,
        };
      }
    }

    if (isInfluence && INFLUENCE) {
      if (match && !match.subtopics) {
        match.subtopics = [];
        match.subtopics.push(INFLUENCE.singular);
      }
    }

    if (!isInfluence && currentTopic.subtopics) {
      for (let i = 0; i < currentTopic.subtopics.length; i++) {
        const currentSubtopic = currentTopic.subtopics[i];
        const subThreshold = _getThreshold(currentSubtopic, thresholdType);
        if (itemTopicScores[currentSubtopic.apiName] >= subThreshold) {
          if (!match) {
            match = {
              primary: currentTopic.singular,
            };
          }
          if (!match.subtopics) {
            match.subtopics = [];
          }
          match.subtopics.push(currentSubtopic.singular);
        }
      }
    }

    if (match) {
      res.push(match);
    }
  }
  return res;
};

function getScoreFromTopicScoresV2(apiName, topicScoresV2) {
  for (let i = 0; i < topicScoresV2.length; i++) {
    const cur = topicScoresV2[i];
    if (cur.name === apiName) {
      return cur;
    }
  }
  return null;
}

const _unjoinTopicScores = (props) => {
  if (!props.topic_scores_v2) {
    props.topic_scores_v2 = [];
  }
  const TOPICS_ARRAY = lodashCloneDeep(_getTopicsArray());
  const INFLUENCES_ARRAY = lodashCloneDeep(_getInfluencesArray());
  const topicsAndInfluences = TOPICS_ARRAY.concat(INFLUENCES_ARRAY);
  for (let i = 0; i < topicsAndInfluences.length; i++) {
    const topic = topicsAndInfluences[i];
    if (topic.parentIds && props.topic_scores.hasOwnProperty(topic.apiName)) {
      if (!getScoreFromTopicScoresV2(topic.apiName, props.topic_scores_v2)) {
        props.topic_scores_v2.push({
          name: topic.apiName,
          score: props.topic_scores[topic.apiName],
        });
      }
      delete props.topic_scores[topic.apiName];
    }
  }
  return props;
};

const _convertTopicScoresFromFieldToApiName = (topic_scores) => {
  let res = {};
  const TOPICS_ARRAY = lodashCloneDeep(_getTopicsArray());
  for (let i = 0; i < TOPICS_ARRAY.length; i++) {
    const topic = TOPICS_ARRAY[i];
    if (topic_scores.hasOwnProperty(topic.databaseV1Name)) {
      res[topic.apiName] = topic_scores[topic.databaseV1Name];
    }
  }

  return res;
};

//TODO: This needs to be show subtopics similar to detected events
const _getTopicsStringFromCategories = (categories) => {
  let dataNeeded = _formatCategoriesForV2(categories);
  let res = "";
  for (let i = 0; i < dataNeeded.length; i++) {
    if (i > 0) {
      res += " | ";
    }
    res += dataNeeded[i].primary;
    if (dataNeeded[i].subtopics) {
      res += " > " + dataNeeded[i].subtopics.join(", ");
    }
  }
  return res;
};

const _formatCategoriesForV2 = (itemCategories) => {
  let res = [];
  const VERIFIED_TOPICS = lodashCloneDeep(_getAllVerifiedTopics());
  for (let i = 0; i < itemCategories.length; i++) {
    let currentTopic = itemCategories[i].name.toLowerCase();
    let currentTopicNameFixed = lodashFind(VERIFIED_TOPICS, [
      "apiName",
      currentTopic,
    ]);
    if (currentTopicNameFixed) {
      res.push(currentTopicNameFixed.display);
    }
  }
  const uniqueList = [...new Set(res)];
  return uniqueList.join();
};

///// TOPICS CONFIG

const _getTopicsConfig = async (force) => {
  if (!topicsConfig || force) {
    try {
      const URL = `${ENDPOINT_CONFIG}/topics?ui=true`;
      const RESULT = await httpGet({ url: URL, noCache: true });
      const { data } = JSON.parse(RESULT);
      topicsConfig = data;
    } catch (error) {
      console.error(error);
      Promise.reject("Issue getting topics config");
    }
  }
};

const _getPrimaryTopicsByEventType = (eventType) => {
  if (eventType === "security") {
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          topic.supportsVerified &&
          topic.supportsPolitical
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  } else if (eventType === "maritime") {
    // maritime only
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          topic.supportsVerified &&
          topic.supportsMaritime
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  } else if (eventType === "verified") {
    // verified only
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          topic.supportsVerified
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
    //TODO is hotspots even a valid eventType?
  } else if (
    eventType === "detected" ||
    eventType === "all" ||
    eventType === "hotspots"
  ) {
    // This supports the UI "all" that supports the combined detected / verified mapping
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          topic.supportsDetected
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  } else {
    throw new Error("The event type supplied is not supported");
  }
};

/*
 *  This will return the default list of topics (detected) if the eventType is not specified
 *  to continue to support existing logic without verified events
 */
const _getPrimaryTopics = (eventType) => {
  if (eventType) {
    return _getPrimaryTopicsByEventType(eventType);
  } else {
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          !topic.supportsVerified
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  }
};

const _appendSubtopics = (_primaryTopic) => {
  let primaryTopic = JSON.parse(JSON.stringify(_primaryTopic));
  if (!primaryTopic.subtopics || !primaryTopic.subtopics instanceof Array) {
    primaryTopic.subtopics = [];
  }
  for (let i = 0; i < topicsConfig.length; i++) {
    const cur = topicsConfig[i];
    if (
      lodashHas(cur, "parentIds") &&
      Array.isArray(cur.parentIds) &&
      cur.parentIds.indexOf(primaryTopic.id) > -1 &&
      !cur.hidden
    ) {
      primaryTopic.subtopics.push(_appendSubtopics(cur));
    }
  }
  return primaryTopic;
};

const _getTopicsArray = (eventType) => {
  if (eventType === "verified") {
    return topicsConfig
      .filter((topic) => {
        return (
          !topic.parentIds &&
          !topic.hidden &&
          !topic.influencerType &&
          topic.supportsVerified
        );
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  } else {
    return topicsConfig
      .filter((topic) => {
        return !topic.hidden && !topic.influencerType;
      })
      .map((topic) => {
        return _appendSubtopics(topic);
      });
  }
};

const _getInfluencesArray = () => {
  const influences = topicsConfig.filter((topic) => {
    return !topic.hidden && topic.influencerType;
  });
  return influences;
};

const _getInfluencersArray = () => {
  const influences = _getInfluencesArray();
  let influencers = [];
  influences.forEach((influence) => {
    if (influence.influencerType) {
      influencers.push({
        type: influence.influencerType.toLowerCase(),
        name: upperCaseFirstLetter(influence.influencerName),
      });
    }
  });
  influencers = lodashUniqBy(influencers, "type");
  return influencers;
};

const _getTopicIcon = (topic, kind = "white") => {
  for (let i = 0; i < SVG_ICONS_TOPICS.length; i++) {
    if (topic === SVG_ICONS_TOPICS[i].Name) {
      return SVG_ICONS_TOPICS[i].Icons[kind];
    }
  }
};

const _getAllVerifiedTopics = () => {
  return _getPrimaryTopicsByEventType("verified");
};

const _getAllDetectedTopics = () => {
  return _getPrimaryTopicsByEventType("detected");
};

/* This returns both detected and verified topics with subtopics */
const _getAllPrimaryTopics = () => {
  const DETECTED_TOPICS = _getPrimaryTopics("detected");
  const VERIFIED_TOPICS = _getPrimaryTopics("verified");
  return [...DETECTED_TOPICS, ...VERIFIED_TOPICS];
};

const _getTopicType = (topic) => {
  if (topic.supportsVerified) {
    return topic.parentIds && topic.parentIds[0]
      ? topic.parentIds[0].replace("verified+", "")
      : topic.type;
  } else {
    return topic.parentIds && topic.parentIds[0]
      ? topic.parentIds[0]
      : topic.type;
  }
};

//////////// - verified events mess below - /////////////////
//TODO all these functions could use a tidy...
//TODO these functions oughta live in bg_topics_util with the tests I wrote
//TODO refactor these to use opts for optional parameters

//TODO this has to be redundant figure out how to replace it
const _myGetTopic = (topicId, idProperty, topics) => {
  let topic = null;
  for (let i = 0; i < topics.length; i++) {
    if (topics[i][idProperty] === topicId) {
      topic = topics[i];
    }
  }
  if (!topic) {
    throw "Couldn't find your topic";
  }
  return topic;
};

//TODO better name...
const _isTopicSelected = (
  topicId,
  selectionString = "",
  topics,
  idProperty = "apiName"
) => {
  let selectedTopics = selectionString ? selectionString.split(",") : [];
  if (selectedTopics.indexOf(topicId) > -1) {
    return true;
  }
  let parents = getParents(_myGetTopic(topicId, idProperty, topics), topics);
  let parentMatch = false;
  parents.forEach((parent) => {
    if (_isTopicSelected(parent[idProperty], (selectionString = ""), topics)) {
      parentMatch = true;
    }
  });
  return parentMatch;
};

//TODO better name...
const _isSubtopicSelected = (
  topicId,
  selectionString = "",
  topics,
  //TODO this probably doesn't actually support variable idProperty regarding selectionString
  idProperty = "apiName"
) => {
  let selectedTopics = selectionString ? selectionString.split(",") : [];

  let topic = _myGetTopic(topicId, idProperty, topics);

  if (topic.parentIds) {
    for (let i = 0; i < topic.parentIds.length; i++) {
      if (selectedTopics.indexOf(topic.parentIds[i]) > -1) {
        return true;
      }
    }
  }

  return _isTopicSelected(topicId, selectionString, topics, idProperty);
};

function _getTopicImmediateChildren(topic, topics) {
  let children = [];
  for (let j = 0; j < topics.length; j++) {
    let childCheckTopc = topics[j];
    if (
      childCheckTopc.parentIds &&
      childCheckTopc.parentIds.indexOf(topic.id) > -1
    ) {
      children.push(childCheckTopc);
    }
  }
  return children;
}

//TODO this is sorta redundant with rollUpChildren but it handles disaster,disaster_earthquake
function removeRedundantChildren(selectedTopics, curTopic, topics, idProp) {
  let res = {
    selectedTopics: selectedTopics,
    changed: false,
  };

  if (selectedTopics.indexOf(curTopic[idProp]) > -1) {
    let children = _getTopicImmediateChildren(curTopic, topics);
    let changed = false;
    children.forEach((child) => {
      let childIndex = selectedTopics.indexOf(child[idProp]);
      if (childIndex > -1) {
        selectedTopics.splice(childIndex, 1);
        changed = true;
      }
    });
    if (changed) {
      return {
        selectedTopics: selectedTopics,
        changed: true,
      };
    }
  }

  return res;
}

function rollUpChildren(selectedTopics, curTopic, topics, idProp) {
  let res = {
    selectedTopics: selectedTopics,
    changed: false,
  };

  let children = _getTopicImmediateChildren(curTopic, topics);

  if (children && children.length > 0) {
    let allChildrenAccountedFor = true;
    children.forEach((child) => {
      if (selectedTopics.indexOf(child[idProp]) < 0) {
        allChildrenAccountedFor = false;
      }
    });
    if (allChildrenAccountedFor) {
      children.forEach((child) => {
        selectedTopics.splice(selectedTopics.indexOf(child[idProp]), 1);
      });
      selectedTopics.push(curTopic[idProp]);
    }
    res = {
      selectedTopics: selectedTopics,
      changed: true,
    };
  }
  return res;
}

//TODO better name. maybe just a real refactor...
function _getTopicsArraySpecial(eventType, verifiedType) {
  let topics = _getTopicsArray(eventType);
  if (verifiedType && verifiedType === "maritime") {
    ///TODO
    return topics.filter((topic) => {
      return topic.supportsMaritime;
    });
  }
  if (verifiedType && verifiedType === "security") {
    return topics.filter((topic) => {
      return topic.supportsPolitical;
    });
  }
  if (eventType && eventType === "detected") {
    return topics.filter((topic) => {
      return topic.supportsDetected;
    });
  }
  return topics;
}

//TODO better name
function _removeMissingTopics(currentSelection, topics, idProp) {
  let res = [];
  currentSelection.forEach((selection) => {
    let accountedFor = false;
    topics.forEach((topic) => {
      if (topic[idProp] === selection) {
        accountedFor = true;
      }
    });
    if (accountedFor) {
      res.push(selection);
    }
  });
  return res;
}

function consolidateSelectedTopics(selectedTopics, topics, idProp) {
  for (let i = 0; i < topics.length; i++) {
    let curTopic = topics[i];

    let rollUpChildrenRes = rollUpChildren(
      selectedTopics,
      curTopic,
      topics,
      idProp
    );
    if (rollUpChildrenRes.changed) {
      selectedTopics = rollUpChildrenRes.selectedTopics;
    }
    let removeRedundantChildrenRes = removeRedundantChildren(
      selectedTopics,
      curTopic,
      topics,
      idProp
    );
    if (removeRedundantChildrenRes.changed) {
      selectedTopics = removeRedundantChildrenRes.selectedTopics;
    }

    //TODO remove redundant children who's parent is in selectedTopics
  }

  //remove exact duplicates
  selectedTopics = [...new Set(selectedTopics)];

  return selectedTopics;
}

function getParents(pTopic, topics) {
  let parents = [];
  if (!pTopic.parentIds) {
    return parents;
  }
  pTopic.parentIds.forEach((parentId) => {
    for (let i = 0; i < topics.length; i++) {
      if (topics[i].id === parentId) {
        parents.push(topics[i]);
      }
    }
  });
  return parents;
}

function getChildren(pTopic, topics) {
  let children = [];
  for (let i = 0; i < topics.length; i++) {
    if (topics[i].parentIds && topics[i].parentIds.indexOf(pTopic.id) > -1) {
      children.push(topics[i]);
    }
  }
  return children;
}

//TODO remove
function handleRemove(topic, currentSelection, topics, idProp) {
  if (currentSelection.indexOf(topic[idProp]) > -1) {
    currentSelection.splice(currentSelection.indexOf(topic[idProp]), 1);
  } else {
    let parents = getParents(topic, topics);

    parents.forEach((parent) => {
      currentSelection = handleRemove(parent, currentSelection, topics, idProp);
      let children = getChildren(parent, topics);
      let otherChildren = children.filter((child) => {
        return child.id !== topic.id;
      });
      otherChildren.forEach((otherChild) => {
        currentSelection.push(otherChild[idProp]);
      });
    });

    let children = _getTopicImmediateChildren(topic, topics);

    children.forEach((child) => {
      let childIndex = currentSelection.indexOf(child[idProp]);
      if (childIndex > -1) {
        currentSelection.splice(childIndex, 1);
      }
    });
  }
  return currentSelection;
}

//TODO remove
function handleAdd(topic, currentSelection, topics, idProp) {
  if (currentSelection.indexOf(topic[idProp] < 0)) {
    currentSelection.push(topic[idProp]);
  }

  let children = getChildren(topic, topics);

  children.forEach((child) => {
    if (currentSelection.indexOf(child[idProp]) > -1) {
      currentSelection.splice(currentSelection.indexOf(child[idProp]), 1);
    }
  });

  return currentSelection;
}

function _modifyTopicString(
  topicId,
  enabled,
  currentSelection,
  topics,
  idProp
) {
  const topic = _myGetTopic(topicId, idProp, topics);
  let currentSelectionSplit = currentSelection
    ? currentSelection.split(",")
    : [];

  if (!enabled) {
    currentSelection = __handleRemoveRecursive(topic, currentSelectionSplit, {
      idProp: "apiName",
      includeAliases: true,
      topics: topics,
    });
  }

  if (enabled && currentSelectionSplit.indexOf(topicId) < 0) {
    currentSelection = __handleAddRecursive(topic, currentSelectionSplit, {
      idProp: "apiName",
      includeAliases: true,
      topics: topics,
    });
  }

  currentSelectionSplit = consolidateSelectedTopics(
    currentSelectionSplit,
    topics,
    idProp
  );

  currentSelectionSplit = _removeMissingTopics(
    currentSelectionSplit,
    topics,
    idProp
  );

  return currentSelectionSplit.sort().join(",");
}

function _getDisplayNameAliases(topic, topics) {
  //TODO dunno how I feel about this. but display is 'Other'
  //for more than one topic and its causing issues
  if (topic.isOther) {
    return [topic];
  }
  let displayName = topic.display;
  let res = [];
  topics.forEach((curTopic) => {
    if (curTopic.display === displayName) {
      res.push(curTopic);
    }
  });
  return res;
}

function _addDisplayNameAliases(currentSelection, opts = {}) {
  let idProp = opts.idProp || "apiName";
  let topics = opts.topics || _getTopicsArray();

  let res = [];
  currentSelection.forEach((curId) => {
    let curTopic = _myGetTopic(curId, idProp, topics);
    if (!res.includes(curId)) {
      res.push(curId);
    }
    let aliases = _getDisplayNameAliases(curTopic, topics);
    aliases.forEach((curAlias) => {
      if (!res.includes(curAlias.display)) {
        res.push(curAlias.display);
      }
    });
  });

  return res;
}

//TODO better name
function __handleAddRecursive(topic, currentSelection, opts = {}) {
  let idProp = opts.idProp || "apiName";
  let includeAliases = opts.includeAliases;
  let topics = opts.topics || _getTopicsArray();

  let topicsToAdd = includeAliases
    ? _getDisplayNameAliases(topic, topics)
    : [topic];
  topicsToAdd.forEach((curTopic) => {
    if (!currentSelection.includes(curTopic[idProp])) {
      currentSelection.push(curTopic[idProp]);
    }
    let children = getChildren(curTopic, topics);
    children.forEach((child) => {
      currentSelection = __handleAddRecursive(
        child,
        currentSelection,
        topics,
        idProp
      );
    });
  });

  return currentSelection;
}

//TODO other stuff that takes idProp oughta have an opts parameter instead of these blown out things
//TODO better name
function __handleRemoveRecursive(topic, currentSelection, opts = {}) {
  let idProp = opts.idProp || "apiName";
  let includeAliases = opts.includeAliases;
  let topics = opts.topics || _getTopicsArray();

  let topicsToRemove = includeAliases
    ? _getDisplayNameAliases(topic, topics)
    : [topic];
  topicsToRemove.forEach((curTopic) => {
    let parents = getParents(curTopic, topics);
    let currentTopicIndex = currentSelection.indexOf(curTopic[idProp]);
    if (currentTopicIndex > -1) {
      currentSelection.splice(currentTopicIndex, 1);
    } else {
      if (!curTopic.parentIds) {
        curTopic.subtopics.forEach((curSub) => {
          let currentSubTopicIndex = currentSelection.indexOf(curSub[idProp]);
          if (currentSubTopicIndex > -1) {
            currentSelection.splice(currentSubTopicIndex, 1);
          }
        });
      } else {
        let siblings = [];
        parents.forEach((curParent) => {
          let siblingsToAdd = getChildren(curParent, topics).filter(
            (subtopicCheck) => {
              return subtopicCheck.id !== topic.id;
            }
          );
          siblingsToAdd.forEach((curSibling) => {
            siblings.push(curSibling);
          });
          currentSelection = __handleRemoveRecursive(
            curParent,
            currentSelection,
            opts
          );
        });
        siblings = lodashUniqBy(siblings, "id");
        siblings.forEach((sibling) => {
          currentSelection = __handleAddRecursive(
            sibling,
            currentSelection,
            opts
          );
        });
      }
    }
  });

  return currentSelection;
}
